import React, {
  ReactElement,
  Ref,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import { Text } from '@adsk/alloy-react-box';
import { useControlled } from '@adsk/alloy-react-helpers';
import { PencilIcon } from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType } from '@adsk/alloy-react-theme';

import { isDefined, isValidValue } from './helpers';
import { InlineEditContainer } from './InlineEditContainer';
import InlineEditContext from './InlineEditContext';
import { defaultRenderInput, defaultValueRenderer } from './renderers';
import { EditProps, InlineEditProps, ValueType } from './types';

/**
 * Inline edit provides an easy way for the user to change displayed content and observe the change in context, without navigating to a separate edit page.
 */
const InlineEditWrapped = <TValue extends ValueType>(
  {
    disabled,
    error,
    editMode: controlledEditMode,
    inputProps: allInputProps = {},
    inputWrapperProps,
    inputStyle,
    buttonProps,
    onChange,
    onEditEnter,
    onEditExit,
    placeholder,
    initialEditMode = false,
    renderInput = defaultRenderInput,
    keepInputMounted,
    renderValue = defaultValueRenderer,
    renderIcon = PencilIcon.renderIcon,
    value,
    showPencilOnHoverOnly,
    saveUnmodifiedValue,
    editOnFocus,
    cancelOnBlur,
    detectUrls = true,
    showUrlsIcons = true,
    softLimit,
    maxLength,
    showCount,
    showCountThreshold,
    style,
    ...props
  }: InlineEditProps<TValue>,
  ref: Ref<HTMLDivElement>,
) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [editMode, setEditMode] = useControlled<boolean>(
    initialEditMode,
    controlledEditMode,
  );

  // Track whether edit mode has ever been active so custom renderers can stay
  // mounted (and preserve internal state such as tab selection) across cycles.
  const hasEnteredEditModeRef = useRef(editMode);
  if (editMode) hasEnteredEditModeRef.current = true;

  const updateEditMode = useCallback(
    (val: boolean) => {
      if (!controlledEditMode) setEditMode(val);
    },
    [setEditMode, controlledEditMode],
  );

  const updateValue = useCallback(
    (newValue: ValueType) => {
      updateEditMode(false);
      if (saveUnmodifiedValue || newValue !== currentValue) {
        setCurrentValue(newValue as TValue);
        onChange?.(newValue as TValue);
      }
      onEditExit?.(newValue as TValue);
    },
    [currentValue, onChange, onEditExit, saveUnmodifiedValue, updateEditMode],
  );

  const { onChange: inputOnChange, ...inputProps } = allInputProps;

  const context = useMemo(
    () => ({
      updateValue,
      setEditMode: updateEditMode,
      inputOnChange,
      onEditExit: (val: ValueType, editProps?: EditProps) =>
        onEditExit?.(val as TValue, editProps),
    }),
    [inputOnChange, onEditExit, updateEditMode, updateValue],
  );
  const onInputClose = () => {
    inputProps?.onClose?.();
    updateEditMode(false);
    onEditExit?.(currentValue);
  };

  const hasValue = isDefined(value) && isValidValue(value);

  const displayMode = !disabled && !editMode;

  const enterEditMode = useCallback(() => {
    if (displayMode) {
      updateEditMode(true);
      onEditEnter?.();
    }
  }, [displayMode, updateEditMode, onEditEnter]);

  const { tokens } = useTheme();
  const { value: valueTokens, icon: iconTokens } =
    tokens?.component.inlineEdit ?? {};

  const shouldKeepInputMounted =
    keepInputMounted ?? renderInput !== defaultRenderInput;

  const input =
    shouldKeepInputMounted || editMode
      ? renderInput({
          onChange: updateValue,
          placeholder,
          style: inputStyle,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore this thinks value is TValue | string, which makes no sense because string is valid ValueType.
          value,
          error,
          onClose: onInputClose,
          saveUnmodifiedValue,
          cancelOnBlur,
          inputProps,
          softLimit,
          maxLength,
          showCount,
          showCountThreshold,
          ...inputWrapperProps,
          editMode,
        })
      : null;

  const displayChrome = (
    <>
      {!editMode && (
        <div
          ref={ref}
          dir="auto"
          css={[
            valueTokens?._,
            !hasValue && placeholder && valueTokens?.placeholder?.true,
          ]}
        >
          <Text detectUrls={detectUrls} showUrlsIcons={showUrlsIcons}>
            {hasValue ? renderValue(value) : placeholder}
          </Text>
        </div>
      )}

      {displayMode &&
        renderIcon({
          size: 20,
          style: [
            iconTokens?._,
            showPencilOnHoverOnly && iconTokens?.showPencilOnHoverOnly?.true,
          ],
        })}
    </>
  );

  const contextualInput = (
    <InlineEditContext.Provider value={context}>
      {input}
    </InlineEditContext.Provider>
  );

  if (shouldKeepInputMounted) {
    return (
      <>
        {!editMode && (
          <InlineEditContainer
            {...buttonProps}
            {...props}
            disabled={disabled}
            error={error}
            editing={false}
            onClick={enterEditMode}
            onFocus={editOnFocus ? enterEditMode : undefined}
            style={style}
          >
            {displayChrome}
          </InlineEditContainer>
        )}

        {hasEnteredEditModeRef.current && (
          <InlineEditContainer
            {...(editMode ? props : {})}
            editing
            disabled={disabled}
            error={error}
            style={editMode ? style : { display: 'none' }}
            aria-hidden={editMode ? undefined : true}
          >
            {contextualInput}
          </InlineEditContainer>
        )}
      </>
    );
  }

  return (
    <InlineEditContainer
      {...(editMode ? {} : buttonProps)}
      {...props}
      disabled={disabled}
      error={error}
      editing={editMode}
      onClick={enterEditMode}
      onFocus={editOnFocus ? enterEditMode : undefined}
      style={style}
    >
      {displayChrome}
      {editMode && contextualInput}
    </InlineEditContainer>
  );
};

const InlineEdit = React.forwardRef<HTMLDivElement, InlineEditProps<null>>(
  InlineEditWrapped,
);

InlineEdit.displayName = 'InlineEdit';

InlineEdit.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Style passed to the input element */
  inputStyle: stylePropType,
  /** Props passed to the input element */
  inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Props passed to the element wrapping the input*/
  inputWrapperProps: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Props passed to the button element */
  buttonProps: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Toggle disabled state */
  disabled: PropTypes.bool,
  /** Toggle error state */
  error: PropTypes.bool,
  /** The value of the field.  Provide string unless renderInput is provided */
  value: PropTypes.any,
  /** If set, editMode becomes externally controlled. */
  editMode: PropTypes.bool,
  /** Callback when value is saved */
  onChange: PropTypes.func,
  /** Callback when entering edit mode */
  onEditEnter: PropTypes.func,
  /** Callback when exiting edit mode */
  onEditExit: PropTypes.func,
  /** Placeholder text value */
  placeholder: PropTypes.string,
  /** Toggle for inital edit mode.  Defaults to false */
  initialEditMode: PropTypes.bool,
  /** render custom editor, called with args ({ placeholder, value, onChange }) */
  renderInput: PropTypes.func,
  /** Keep the input mounted (hidden) while not editing so it preserves internal state across close/reopen. Defaults to true for a custom renderInput and false for the default renderer; an explicitly passed value always wins, so keepInputMounted={false} restores unmount-on-exit for custom inputs that open themselves on mount. */
  keepInputMounted: PropTypes.bool,
  /** Transform function for display of value */
  renderValue: PropTypes.func,
  /** Renders a custom icon */
  renderIcon: PropTypes.func,
  /** Whether the pencil icon should be hidden by default and only shown on hover state */
  showPencilOnHoverOnly: PropTypes.bool,
  /** If true the initial value can be saved without being modified, onChange will be fired when saving.
   * By default (false) the user must modify the value before it can be saved */
  saveUnmodifiedValue: PropTypes.bool,
  /** Whether to enter edit mode immediately when focused */
  editOnFocus: PropTypes.bool,
  /** If true the initial value will be restored when modified input is blurred */
  cancelOnBlur: PropTypes.bool,
  /** Whether to automatically detect and render URLs as links with an icon (enabled by default) */
  detectUrls: PropTypes.bool,
  /** Whether to show icons next to URLs (enabled by default) */
  showUrlsIcons: PropTypes.bool,
  /** Like maxLength but this is a soft threshold and the input does not stop the user from entering characters beyond this amount, but this will show as the maximum number in the count display */
  softLimit: PropTypes.number,
  /** A hard limit on the maximum number of characters permitted in the input field, if softLimit is not provided then this will show as the maximum number in the count display */
  maxLength: PropTypes.number,
  /** If true and maxLength or softLimit is set then display a character count, otherwise do not display the character count */
  showCount: PropTypes.bool,
  /** If set, this will delay the display of character count until the character count is within this many characters of reaching the maximum length or soft limit length. Default is 15. Set to 0 to always show the count when showCount is enabled. */
  showCountThreshold: PropTypes.number,
};

export default InlineEdit as <T extends ValueType>(
  p: InlineEditProps<T> & { ref?: Ref<HTMLDivElement> },
) => ReactElement;
